import React, { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase'; // Make sure this path is correct
import styles from './WaitlistPage.module.css';

const benefits = [
  {
    title: 'Free Lifetime Access for the First 100',
    desc: 'The first 100 people on the waiting list will unlock full lifetime access to LeanWeek-AI at no cost.',
    highlight: 'Top Priority',
    featured: true,
  },
  {
    title: 'Founding Member Pricing',
    desc: 'Join early and secure launch pricing before public release. Early members get the best long-term value.',
    highlight: 'Save at Launch',
  },
  {
    title: 'Early Product Access',
    desc: 'Get access to the app before the public launch and experience new features before everyone else.',
    highlight: 'Beta Access',
  },
  {
    title: 'Priority Feature Drops',
    desc: 'Be first in line for premium exports, AI planning upgrades, and new check-in experiences as they roll out.',
    highlight: 'First Look',
  },
  {
    title: 'Direct Product Influence',
    desc: 'Early users help shape the roadmap. Your feedback can directly influence features and UX decisions.',
    highlight: 'Your Voice Matters',
  },
  {
    title: 'Exclusive Founder Updates',
    desc: 'Get curated launch updates, progress notes, and behind-the-scenes product milestones without spam.',
    highlight: 'Insider Updates',
  },
];

const steps = [
  {
    title: 'Join the waiting list',
    desc: 'Submit your name and email to reserve your place before public launch.',
  },
  {
    title: 'Watch for your invite',
    desc: 'We will email waitlist members first when early access opens.',
  },
  {
    title: 'Claim your early perks',
    desc: 'Eligible members will receive launch bonuses, including the first-100 lifetime access offer.',
  },
];

const WaitlistPage = () => {
  // 1. Added 'platform' to initial state
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    platform: '', 
  });

  // UI States
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.firstName || !form.email || !form.platform) {
      setErrorMessage('Please fill out all fields.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const cleanEmail = form.email.trim().toLowerCase();
      
      // 1. Create a reference where the Document ID is the email address
      const docRef = doc(db, 'waitingList', cleanEmail);

      // 2. Use setDoc instead of addDoc
      await setDoc(docRef, {
        firstName: form.firstName.trim(),
        email: cleanEmail,
        platform: form.platform,
        createdAt: serverTimestamp(),
      });
      
      setIsSuccess(true);
      setForm({ firstName: '', email: '', platform: '' }); 
      
    } catch (error) {
      console.error('Waitlist submission error:', error);
      
      // 3. Catch the specific error when a duplicate is blocked by rules
      if (error.code === 'permission-denied') {
        setErrorMessage("You're already on the list! Keep an eye on your inbox.");
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroInner}>
            <span className={styles.eyebrow}>Early Access</span>

            <div className={styles.socialProof}>
              <div className={styles.avatarStack}>
                <span>JC</span>
                <span>MB</span>
                <span>AL</span>
              </div>
              <p>Join the early list and secure your spot before public launch.</p>
            </div>

            <h1 className={styles.title}>
              Get Early Access to <span>LeanWeek-AI</span>
            </h1>

            <p className={styles.subtitle}>
              Join the waiting list for priority access, launch pricing perks,
              and exclusive early-member benefits designed for the first wave of
              LeanWeek users.
            </p>

            {isSuccess ? (
              <div className={styles.formCard} style={{ textAlign: 'center', padding: '40px 20px' }}>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '1.5rem' }}>You're on the list! 🎉</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Keep an eye on your inbox. We will email you as soon as early access opens.
                </p>
              </div>
            ) : (
              <form className={styles.formCard} onSubmit={handleSubmit}>
                <div className={styles.formGrid}>
                  
                  {/* Name Field */}
                  <div className={styles.field}>
                    <label htmlFor="firstName">First Name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="Your first name"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* Email Field */}
                  <div className={styles.field}>
                    <label htmlFor="email">Email Address</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* Platform Field (Spans full width) */}
                  <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="platform">Primary Device</label>
                    <select
                      id="platform"
                      name="platform"
                      value={form.platform}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        padding: '14px',
                        borderRadius: '12px',
                        border: '1px solid var(--border-subtle)',
                        background: 'var(--bg-dark)',
                        color: form.platform ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontSize: '1rem',
                        marginTop: '8px',
                        outline: 'none',
                        appearance: 'none', // Removes default browser styling
                        cursor: 'pointer'
                      }}
                    >
                      <option value="" disabled>Select your platform</option>
                      <option value="iOS">Apple iOS (iPhone)</option>
                      <option value="Android">Android</option>
                    </select>
                  </div>
                </div>

                {errorMessage && (
                  <p style={{ color: '#ff4d4d', margin: '10px 0', fontSize: '0.9rem' }}>
                    {errorMessage}
                  </p>
                )}

                <button 
                  type="submit" 
                  className={styles.primaryBtn}
                  disabled={isLoading}
                  style={{ opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer', marginTop: '16px' }}
                >
                  {isLoading ? 'Securing your spot...' : 'Join the Waitlist'}
                </button>

                <p className={styles.formNote}>
                  No spam. Only launch updates, early access invites, and member benefits.
                </p>
              </form>
            )}

            <div className={styles.heroHighlight}>
              <div className={styles.highlightBadge}>Limited Founder Perk</div>
              <h2>First 100 people get free lifetime access.</h2>
              <p>
                This is the headline incentive for joining now. Once the first
                100 spots are gone, that offer closes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits, Timeline, and Final CTA sections remain exactly the same */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionKicker}>Why Join</span>
            <h2>Benefits for early members</h2>
            <p>
              The waiting list is more than a sign-up form. It is the entry
              point for launch rewards, access priority, and founder-level perks.
            </p>
          </div>

          <div className={styles.benefitsGrid}>
            {benefits.map((benefit) => (
              <article
                key={benefit.title}
                className={`${styles.benefitCard} ${
                  benefit.featured ? styles.featuredCard : ''
                }`}
              >
                <div className={styles.cardTop}>
                  <span className={styles.cardPill}>{benefit.highlight}</span>
                  {benefit.featured && (
                    <span className={styles.featuredTag}>Best Reason to Join</span>
                  )}
                </div>

                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.timelineSection}>
        <div className={styles.container}>
          <div className={styles.timelineWrap}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionKicker}>How It Works</span>
              <h2>Three simple steps</h2>
              <p>
                Joining the waitlist is fast, and the early access process is simple.
              </p>
            </div>

            <div className={styles.timelineGrid}>
              {steps.map((step, index) => (
                <div key={step.title} className={styles.stepCard}>
                  <div className={styles.stepNumber}>0{index + 1}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.finalCtaSection}>
        <div className={styles.container}>
          <div className={styles.finalCta}>
            <span className={styles.sectionKicker}>Do Not Miss It</span>
            <h2>Reserve your place before the first 100 fill up.</h2>
            <p>
              Join now to maximize your chance of getting free lifetime access
              and priority entry into LeanWeek-AI.
            </p>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
              className={styles.secondaryBtn}
              style={{ cursor: 'pointer' }}
            >
              Join Early Access
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WaitlistPage;